import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject, Subscription, timer } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { pdfPaperSizes } from 'src/app/dashboards/dialogs/pdf-export/pdf-export.dialog';
import { ExportVisualDataTableFormat, ExportVisualFileRequestDto, JobStatus, PdfPaperSize, SetVisualRequestDto, UserAppFeaturesDto, UserRole, VisualExportFileFormat, VisualExportService, VisualExportStyle, VisualService } from 'src/app/services/api.services';
import { SnackService } from 'src/app/services/snack.service';
import { PbiChartTypes, VisualChartType, getInsightsPbiChartType, pbiChartEquals } from 'src/app/shared/chart/chart';
import { AppUser } from 'src/app/shared/models/app-user';
import { AppActions, AppSelectors } from 'src/app/state/app';
import { catchAppError, downloadFile } from '../utils';
import { VisualFileExportDialogOptions, VisualFileExportDialogService } from './visual-data-export.service';
import { logger } from '../../pbir/utils/logger';

const LOGGER = logger('VisualFileExportDialogComponent');

@Component({
  selector: 'app-visual-data-export-dialog',
  templateUrl: './visual-data-export-dialog.component.html',
  styleUrls: ['./visual-data-export-dialog.component.scss'],
  standalone: false
})
export class VisualFileExportDialogComponent implements OnDestroy {
  subs$: Subscription[] = [];

  user: AppUser;
  features: UserAppFeaturesDto = {};

  constructor(
    public store: Store,
    public visualFileExportDialogService: VisualFileExportDialogService,
    public visualExportService: VisualExportService,
    public dialogRef: MatDialogRef<VisualFileExportDialogComponent>,
    public snackService: SnackService,
    public visualService: VisualService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: VisualFileExportDialogData) {
    this.dialogRef.afterClosed().subscribe(() => {
      this.abortJob();
    });

    this.dialogRef.addPanelClass('settings-modal-dialog-panel');

    this.store
      .select(AppSelectors.getFeatures)
      .subscribe(f => {
        this.features = f;
        this.validate();
      });

    this.store
      .select(AppSelectors.getUser)
      .subscribe(u => {
        this.user = u;
        this.insightsAdmin = u.role >= UserRole.InsightsAdmin;
        this.validate();
      });

    this.store
      .select(AppSelectors.getUserState)
      .subscribe(us => {
        this.paperSize = us?.defaultPdfPaperSize || PdfPaperSize.A4;
      });

    this.getVisualToExport(data.options)
      .pipe(
        tap(( visual ) => {
          this.visualInfo = visual;
          this.loadDefaults();
          this.loading = false;
        }),
        catchAppError(err => this.setFailed(err), { showErrorSnack: false })
      ).subscribe();
  }

  ngOnDestroy() {
    this.subs$.forEach(s => s.unsubscribe());
  }
  loading = true;

  visualInfo: exportVisualInfo;

  insightsAdmin = false;

  formats = VisualFileExportFormats;
  format = VisualExportFileFormat.Excel;

  paperSizes = pdfPaperSizes;
  paperSize: PdfPaperSize = this.data.options.paperSize || PdfPaperSize.A4;

  includeFooter = this.data.options.includeFooter ?? true;
  includeHeader = this.data.options.includeHeader ?? true;
  chartImage = false;
  style = true;

  valid = true;
  exporting = false;

  success = false;
  failed = false;

  downloadUrl: string;
  fileName: string;

  title = this.data.title;

  tableFormat = this.data.options.tableFormat ?? ExportVisualDataTableFormat.Default;
  tableFormats = [];

  imageDpi = 300;
  imageDpis = [
    {
      value: 72,
      title: '72',
    },
    {
      value: 96,
      title: '96',
    },
    {
      value: 150,
      title: '150',
    },
    {
      value: 300,
      title: '300',
    },
  ];

  pivotTableExport = false;
  canSetLayout = false;

  showDPI = false;
  layoutOptions = false;

  currentJobId: number;

  abort$: Subject<void>;
  aborted = false;

  locale = this.data.options.locale;

  private abortJob() {
    this.aborted = true;
    this.abort$?.next();

    if (this.currentJobId) {
      this.visualExportService.cancelFileExport(this.currentJobId).subscribe();
      this.currentJobId = 0;
    }
  }

  loadDefaults() {
    if (pbiChartEquals(this.visualInfo.visualType, PbiChartTypes.table)) {
      this.tableFormat = ExportVisualDataTableFormat.Default;
    }
    else if (pbiChartEquals(this.visualInfo.visualType, PbiChartTypes.pivotTable)) {
      this.tableFormat = ExportVisualDataTableFormat.PivotTable;
    }
    else if (pbiChartEquals(this.visualInfo.visualType, PbiChartTypes.barChart) 
    || pbiChartEquals(this.visualInfo.visualType, PbiChartTypes.columnChart)
    || pbiChartEquals(this.visualInfo.visualType, PbiChartTypes.card)) {
      this.format = VisualExportFileFormat.Pdf;
      this.tableFormat = ExportVisualDataTableFormat.Default;
    }
    else {
      this.format = VisualExportFileFormat.Pdf;
      this.tableFormat = ExportVisualDataTableFormat.PivotTable;
    }
    this.modelChanged();
  }

  validate() {
    this.formats = VisualFileExportFormats.filter(f => {
      if (f.value == VisualExportFileFormat.Png)
        return !!this.features?.visualExport?.pngExport;

      return true;
    });

    this.tableFormats = ExportVisualDataTableFormats;
    this.tableFormats = ExportVisualDataTableFormats.filter(f => {
      return (f.value != ExportVisualDataTableFormat.PivotTable || !!this.features?.visualExport?.pivotTableExport);
    });

    this.format = this.formats.find(f => f.value == this.format) ? this.format : this.formats[0].value;
    this.tableFormat = this.tableFormats.find(f => f.value == this.format) ? this.tableFormat : this.tableFormats[0].value;

    if (![VisualExportFileFormat.Csv, VisualExportFileFormat.Excel].includes(this.format)) {
      this.tableFormats = [];
    }
     

    if (!this.features?.visualExport?.excelChartImage)
      this.chartImage = false;

    if (!this.features?.visualExport?.exportLayout) {
      this.includeHeader = true;
      this.includeFooter = true;
      this.imageDpi = this.imageDpis[this.imageDpis.length - 1].value;
    }

    this.modelChanged();
  }

  modelChanged() {
    this.pivotTableExport = this.format == VisualExportFileFormat.Excel && this.tableFormat == ExportVisualDataTableFormat.PivotTable;
    //DPI Currently turned off
    //this.showDPI = this.format == VisualExportFileFormat.Png;
    this.layoutOptions = this.showDPI || this.format == VisualExportFileFormat.Pdf || (this.format == VisualExportFileFormat.Excel && this.chartImage);
  }

  cancel() {
    this.abortJob();

    this.dialogRef.close();
  }

  next() {
    if (this.exporting)
      return false;

    if (this.success) {
      this.dialogRef.close();
    }
    else {
      this.export();
    }
  }

  getImageRenderAtText() {
    let w, h;

    if (this.paperSize == PdfPaperSize.A4) {
      w = 21;
      h = 29.7;
    }
    else if (this.paperSize == PdfPaperSize.Letter) {
      w = 21.59;
      h = 27.94;
    }

    const CM_TO_INCH = 0.393701

    w = Math.ceil(w * CM_TO_INCH * this.imageDpi);
    h = Math.ceil(h * CM_TO_INCH * this.imageDpi);

    return `The image will be rendered at ${w}px x ${h}px.`;
  }

  private export() {
    this.abortJob();

    this.aborted = false;
    this.abort$ = new Subject<void>();

    let request = this.beginExport()

    this.visualExportService.exportFile(request).pipe(
      tap(({ id }) => this.currentJobId = id),
      switchMap(({ id }) =>
        timer(0, 5000).pipe(
          takeUntil(this.abort$),
          switchMap(() => this.pullExportStatus(id))
        )
      ),
      catchAppError(err => this.setFailed(err), { showErrorSnack: false })
    ).subscribe();
  }

  pullExportStatus(id: number) {
    return this.visualExportService.getExportStatus(id).pipe(
      takeUntil(this.abort$),
      tap(({ status, error }) => {
        if (this.aborted)
          return;

        if (status === JobStatus.Finished) {
          this.visualExportService.getExportResult(id).pipe(
            takeUntil(this.abort$),
            tap(({ fileUrl, fileName }) => {
              if (this.aborted)
                return;

              this.setSuccess();

              this.downloadUrl = fileUrl;
              this.fileName = fileName;

              try {
                downloadFile(this.downloadUrl, this.fileName);
              }
              catch (err) {
                LOGGER.error('downloadFile failed', err);
              }
            }),
            catchAppError(err => this.setFailed(err), { showErrorSnack: false })
          ).subscribe();
        }
        else if (status === JobStatus.Failed) {
          this.setFailed(error);
        }
      }),
      catchAppError(err => this.setFailed(err), { showErrorSnack: false })
    );
  }

  setSuccess() {
    this.failed = false;
    this.exporting = false;
    this.success = true;
    this.currentJobId = null;

    this.abortJob();
  }

  setFailed(error: any) {
    this.failed = true;
    this.exporting = false;

    this.abortJob();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload($event: Event) {
    if (this.exporting) {
      this.snackService.open('Leaving this page will cancel the export.', {
        panelClass: 'trg-snack-warning',
        verticalPosition: 'bottom',
      });

      $event.returnValue = false;
    }
  }

  private getVisualToExport(options: VisualFileExportDialogOptions) {
    if ('visualId' in options) {
      return this.visualService.getVisual(options.visualId, false).pipe(
        map(v => (<exportVisualInfo>{
          visualId: v.id,
          visualName: v.name,
          visualType: v.content.type,
          pbiChartType: getInsightsPbiChartType(v.content.type)
        }))
      );
    }
    else if ('visual' in options) {
      let setVisualRequest: SetVisualRequestDto = {
        visual: {
          ...options.visual,
          
          createdBy: this.user.username,

          categoryIds: [],
          sample: false,
          fromLibrary: false,
          shared: false,

          id: 0,
          name: options.visual.name,
          tempVisual: true
        }
      };

      return this.visualService.setVisual(setVisualRequest).pipe(
        map(v => (<exportVisualInfo>{
          visualId: v.id,
          visualName: v.name,
          visualType: v.content.type,
          pbiChartType: getInsightsPbiChartType(v.content.type)
        }))
      );
    }
  }

  private beginExport() {
    this.abort$?.next();
    this.abort$ = new Subject<void>();

    this.exporting = true;
    this.failed = false;
    this.success = false;

    this.store.dispatch(AppActions.updateUserState({
      userState: {
        defaultPdfPaperSize: this.paperSize,
      }
    }));

    let format = this.format;
    let tableFormat = this.tableFormat;

    let chartImage = this.chartImage;

    if (!this.features.visualExport?.excelChartImage || this.format !== VisualExportFileFormat.Excel) {
      this.chartImage = false;
    }

    let includeHeader = this.includeHeader;
    let includeFooter = this.includeFooter;

    if (!this.features?.visualExport?.exportLayout) {
      let includeHeaderFooter = this.format == VisualExportFileFormat.Pdf;
      includeHeader = includeHeaderFooter;
      includeFooter = includeHeaderFooter;
    }

    let options = { ...this.data.options };

    return <ExportVisualFileRequestDto>{
      visualId: this.visualInfo.visualId,
      filters: this.data.options.filters ?? [],
      fileName: this.visualInfo.visualName,
      format,
      includeHeader,
      includeFooter,
      paperSize: this.paperSize,
      customFields: this.data.options.customFields,
      renderUnshared: this.data.options.renderUnshared,
      tableFormat,
      chartImage,
      imageDpi: this.imageDpi,
      style: this.style ? VisualExportStyle.Default : VisualExportStyle.None,
      locale: this.locale?.id
    };
  }
}

export interface VisualFileExportDialogData {
  options: VisualFileExportDialogOptions;
  title: string;
}

export interface VisualFileExportDialogResult {
  format: VisualExportFileFormat;
  paperSize?: PdfPaperSize;
  includeFooter?: boolean;
  includeHeader?: boolean;
}

const VisualFileExportFormats = [
  {
    value: VisualExportFileFormat.Excel,
    title: '.xlsx (Excel)'
  },
  {
    value: VisualExportFileFormat.Csv,
    title: '.csv (Text)'
  },
  {
    value: VisualExportFileFormat.Pdf,
    title: '.pdf (PDF)'
  },
  {
    value: VisualExportFileFormat.Png,
    title: '.png (Image)'
  }
];

export const ExportVisualDataTableFormats = [
  {
    value: ExportVisualDataTableFormat.Default,
    title: 'Table'
  },
  {
    value: ExportVisualDataTableFormat.PivotTable,
    title: 'Pivot Table'
  }
];

const ImageDpis = [
  {
    value: 72,
    title: '72',
  },
  {
    value: 96,
    title: '96',
  },
  {
    value: 150,
    title: '150',
  },
  {
    value: 300,
    title: '300',
  },
];

interface exportVisualInfo {
  visualId: number;
  visualName: string;
  visualType: string;
  pbiChartType: VisualChartType
}
